import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Check, Copy, Loader2, CreditCard, QrCode } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { isValidCpf, formatCpf, formatPhoneBR } from "@/lib/validation/cpf";
import { isPaymentUiBlocked } from "@/lib/platform";
import { analytics } from "@/lib/analytics";
import { metaIdentifyPhone, metaTrack } from "@/lib/metaPixel";
import { PREMIUM_PLANS, formatBRL, PREMIUM_OFFER_PLAN_ID, type PaymentMethod } from "@/config/pricing";

type Stage = "form" | "loading" | "awaiting" | "pix" | "success" | "error";

interface PixPayload {
  encodedImage: string;
  payload: string;
  expirationDate: string;
}

const CheckoutPremium: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tf } = useLanguage();
  const { isPremium, refresh: refreshSubscription } = useSubscription();

  // Paywall placement forwarded by Pricing (which got it from PaywallSheet) —
  // stamps checkout_completed.source. Absent = organic checkout entry.
  const paywallSource = (location.state as { paywallPlacement?: string } | null)?.paywallPlacement;

  const [stage, setStage] = useState<Stage>("form");
  const plan = PREMIUM_OFFER_PLAN_ID;
  const [method, setMethod] = useState<PaymentMethod>("card");

  // Customer fields
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Card-only fields
  const [cardNumber, setCardNumber] = useState("");
  const [cardExp, setCardExp] = useState(""); // MM/YY
  const [cardCcv, setCardCcv] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [addressNumber, setAddressNumber] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorIsAlreadyPremium, setErrorIsAlreadyPremium] = useState(false);
  const [pix, setPix] = useState<PixPayload | null>(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState<number>(Date.now());

  useEffect(() => {
    if (isPaymentUiBlocked()) {
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  // Auth gate + prefill
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (cancelled) return;
      if (!user) {
        navigate("/auth");
        return;
      }
      setEmail(user.email || "");
      const fullName = (user.user_metadata as Record<string, unknown> | undefined)?.full_name;
      if (typeof fullName === "string" && fullName.length > 0) {
        setName(fullName);
        setCardHolder(fullName);
      }
    });
    return () => { cancelled = true; };
  }, [navigate]);

  // Webhook flips status -> useSubscription emits update -> success.
  useEffect(() => {
    if ((stage === "pix" || stage === "awaiting") && isPremium) {
      setStage("success");
      analytics.subscriptionActivated("premium");
      // Funnel closer (12/07): the client-observed payment confirmation,
      // attributed to the paywall placement that started the flow. Fires
      // once — the stage transition makes this condition false afterwards.
      analytics.checkoutCompleted("premium", paywallSource);
    }
  }, [stage, isPremium, navigate, paywallSource]);

  useEffect(() => {
    if (stage !== "success") return;
    const tid = setTimeout(() => navigate("/home"), 2000);
    return () => clearTimeout(tid);
  }, [stage, navigate]);

  // PIX SAFETY NET (11/06, provado com pagamento real): o webhook ativou
  // Premium em ~5s no banco mas o modal ficou em "Aguardando" — o
  // useSubscription faz fetch único e o channel Realtime não entrega em prod,
  // então isPremium nunca virou com o modal aberto. Enquanto o estágio pix
  // estiver visível, re-consulta a row de subscriptions a cada 4s via
  // refresh() — o MESMO SELECT + regra de acesso do hook (plan pago +
  // current_period_end futuro), ou seja, exatamente o que o asaas-webhook
  // grava no PAYMENT_CONFIRMED/RECEIVED. O effect acima cuida da transição
  // de sucesso; cleanup garantido no close/unmount via deps de stage.
  useEffect(() => {
    if (stage !== "pix" && stage !== "awaiting") return;
    const id = setInterval(() => refreshSubscription(), 4000);
    return () => clearInterval(id);
  }, [stage, refreshSubscription]);

  // Already premium short-circuit.
  useEffect(() => {
    if (stage === "form" && isPremium) {
      setErrorIsAlreadyPremium(true);
      setErrorMessage(t("checkout.error.already_premium"));
      setStage("error");
    }
  }, [stage, isPremium, t]);

  useEffect(() => {
    if (stage !== "pix") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [stage]);

  const expiresLabel = useMemo(() => {
    if (!pix?.expirationDate) return "";
    const ms = new Date(pix.expirationDate).getTime() - now;
    if (Number.isNaN(ms) || ms <= 0) return "00:00";
    // Asaas pode devolver expiração meses à frente (pix de assinatura) — o
    // contador cru de minutos renderizou "Expira em 525865:29" em prod.
    // Acima de 24h não é informação de urgência: esconde a linha inteira.
    if (ms > 24 * 60 * 60 * 1000) return "";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60).toString().padStart(2, "0");
    const s = (totalSec % 60).toString().padStart(2, "0");
    return h > 0 ? h + ":" + m + ":" + s : m + ":" + s;
  }, [pix, now]);

  const planValue = PREMIUM_PLANS[plan].value;
  const submitLabel = method === "pix"
    ? t("checkout.form.submit.pix")
    : t("checkout.form.submit.card");

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (name.trim().length < 3) next.name = t("checkout.form.errors.min_length");
    if (!isValidCpf(cpf)) next.cpf = t("checkout.form.errors.invalid_cpf");
    if (phone.length > 0) {
      const d = phone.replace(/\D/g, "");
      if (d.length !== 10 && d.length !== 11) next.phone = t("checkout.form.errors.invalid_phone");
    }

    if (method === "card") {
      if (cardHolder.trim().length < 3) next.cardHolder = t("checkout.form.errors.min_length");
      const num = cardNumber.replace(/\s/g, "");
      if (!/^\d{13,19}$/.test(num)) next.cardNumber = t("checkout.form.errors.invalid_card_number");
      const expMatch = cardExp.match(/^(\d{2})\s*\/\s*(\d{2,4})$/);
      if (!expMatch) {
        next.cardExp = t("checkout.form.errors.invalid_card_exp");
      } else {
        const mm = parseInt(expMatch[1], 10);
        if (mm < 1 || mm > 12) next.cardExp = t("checkout.form.errors.invalid_card_exp");
      }
      if (!/^\d{3,4}$/.test(cardCcv)) next.cardCcv = t("checkout.form.errors.invalid_card_ccv");
      const cep = postalCode.replace(/\D/g, "");
      if (cep.length !== 8) next.postalCode = t("checkout.form.errors.invalid_postal_code");
      if (addressNumber.trim().length === 0) next.addressNumber = t("checkout.form.errors.required");
      // Card requires holder phone for Asaas; require customer phone too.
      if (phone.replace(/\D/g, "").length < 10) {
        next.phone = t("checkout.form.errors.required_for_card");
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // Meta mirror: payment info submitted (browser event with eventID for
    // dedupe headroom). Phone Advanced Matching uses the E.164 the form
    // already collected — raises match quality for the server-side Purchase.
    metaTrack("AddPaymentInfo", { content_category: "premium", currency: "BRL" });
    const phDigits = phone.replace(/\D/g, "");
    if (phDigits.length >= 10) void metaIdentifyPhone("55" + phDigits);
    setStage("loading");
    setErrorMessage(null);
    setErrorIsAlreadyPremium(false);

    const body: Record<string, unknown> = {
      plan,
      method,
      name: name.trim(),
      cpf: cpf.replace(/\D/g, ""),
      email,
      phone: phone ? phone.replace(/\D/g, "") : undefined,
    };

    if (method === "card") {
      const expMatch = cardExp.match(/^(\d{2})\s*\/\s*(\d{2,4})$/)!;
      body.card = {
        holderName: cardHolder.trim(),
        number: cardNumber.replace(/\s/g, ""),
        expiryMonth: expMatch[1],
        expiryYear: expMatch[2],
        ccv: cardCcv,
      };
      body.postalCode = postalCode.replace(/\D/g, "");
      body.addressNumber = addressNumber.trim();
      body.phoneHolder = phone.replace(/\D/g, "");
    }

    try {
      const { data, error } = await supabase.functions.invoke("asaas-create-subscription", { body });
      if (error) {
        const ctx = (error as Record<string, unknown>).context;
        const status = ctx && typeof ctx === "object" ? (ctx as { status?: number }).status : undefined;
        if (status === 409) {
          setErrorIsAlreadyPremium(true);
          setErrorMessage(t("checkout.error.already_premium"));
        } else if (method === "card") {
          // Try to extract Asaas hint from response (best-effort).
          let hint: string | null = null;
          try {
            const resp = await (ctx as { json?: () => Promise<{ hint?: string }> } | undefined)?.json?.();
            if (resp?.hint) hint = resp.hint;
          } catch { /* ignore */ }
          setErrorMessage(hint || t("checkout.error.card_declined"));
        } else {
          setErrorMessage(t("checkout.error.generic"));
        }
        setStage("error");
        return;
      }

      if (method === "pix") {
        const pixPayload = (data as { pix?: PixPayload } | null)?.pix;
        if (!pixPayload?.encodedImage || !pixPayload?.payload) {
          setErrorMessage(t("checkout.error.generic"));
          setStage("error");
          return;
        }
        setPix(pixPayload);
        setStage("pix");
      } else {
        // A created subscription is not a confirmed payment. Keep checking
        // the server-backed entitlement; elapsed time must never mean success.
        setStage("awaiting");
      }
    } catch (err) {
      console.error("[CheckoutPremium] invoke threw", err);
      setErrorMessage(t("checkout.error.generic"));
      setStage("error");
    }
  };

  const handleCopy = async () => {
    if (!pix?.payload) return;
    try {
      await navigator.clipboard.writeText(pix.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("[CheckoutPremium] clipboard.writeText failed", err);
    }
  };

  const handleRetry = () => {
    if (errorIsAlreadyPremium) {
      navigate("/home");
      return;
    }
    setErrorMessage(null);
    setStage("form");
  };

  // Apple 3.1.1 — suppress first-frame flash of the price-bearing checkout
  // on iOS native. useEffect above redirects, but mounting renders one frame.
  if (isPaymentUiBlocked()) return null;

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #0d1117 100%)" }}
    >
      <button
        onClick={() => navigate(-1)}
        aria-label="Close"
        className="fixed right-4 z-50 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 1rem)",
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <X size={16} className="text-white/60" />
      </button>

      <div
        className="relative z-10 px-5 pb-12 max-w-md mx-auto"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 3rem)" }}
      >
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Sparkles size={18} style={{ color: "hsl(187 72% 55%)" }} />
            <span
              className="text-[10px] font-display tracking-[0.25em] uppercase"
              style={{ color: "hsl(187 72% 50% / 0.7)" }}
            >
              VYV Premium
            </span>
          </div>
          <h1 className="font-display text-[24px] font-bold text-white leading-tight mb-2">
            {t("checkout.title")}
          </h1>
          <p className="text-[13px] font-body" style={{ color: "hsl(210 20% 65%)" }}>
            {t("checkout.subtitle")}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {stage === "form" && (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              onSubmit={handleSubmit}
              className="rounded-2xl p-5 flex flex-col gap-4"
              style={{
                background: "rgba(255,255,255,0.03)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1.5px solid rgba(0,229,255,0.18)",
              }}
            >
              <div className="rounded-xl border border-primary/30 bg-primary/10 p-4">
                <p className="text-sm text-white/70">{t("checkout.plan.monthly")}</p>
                <p className="text-xl font-semibold text-white">{formatBRL(planValue)}{t("checkout.plan.perMonthSuffix")}</p>
              </div>

              <MethodToggle method={method} setMethod={setMethod} t={t} />

              {method === "pix" && (
                <div
                  className="rounded-lg px-3 py-2 text-[11px] font-body leading-snug"
                  style={{
                    background: "rgba(255,200,80,0.06)",
                    border: "1px solid rgba(255,200,80,0.18)",
                    color: "hsl(45 90% 75%)",
                  }}
                >
                  {t("checkout.pix.honesty.monthly")}
                </div>
              )}

              <Field
                label={t("checkout.form.name")}
                value={name}
                onChange={setName}
                error={errors.name}
                autoComplete="name"
              />
              <Field
                label={t("checkout.form.cpf")}
                value={formatCpf(cpf)}
                onChange={(v) => setCpf(v.replace(/\D/g, "").slice(0, 11))}
                error={errors.cpf}
                inputMode="numeric"
              />
              <Field
                label={t("checkout.form.email")}
                value={email}
                onChange={() => undefined}
                readOnly
                disabled
                autoComplete="email"
              />
              <Field
                label={method === "card" ? t("checkout.form.phone.required") : t("checkout.form.phone")}
                value={formatPhoneBR(phone)}
                onChange={(v) => setPhone(v.replace(/\D/g, "").slice(0, 11))}
                error={errors.phone}
                inputMode="tel"
                autoComplete="tel"
                optional={method !== "card"}
              />

              {method === "card" && (
                <>
                  <div className="h-px w-full my-1" style={{ background: "rgba(255,255,255,0.06)" }} />
                  <Field
                    label={t("checkout.form.card.holder")}
                    value={cardHolder}
                    onChange={setCardHolder}
                    error={errors.cardHolder}
                    autoComplete="cc-name"
                  />
                  <Field
                    label={t("checkout.form.card.number")}
                    value={formatCardNumber(cardNumber)}
                    onChange={(v) => setCardNumber(v.replace(/\D/g, "").slice(0, 19))}
                    error={errors.cardNumber}
                    inputMode="numeric"
                    autoComplete="cc-number"
                  />
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Field
                        label={t("checkout.form.card.exp")}
                        value={formatExp(cardExp)}
                        onChange={(v) => setCardExp(formatExpEdit(v))}
                        error={errors.cardExp}
                        inputMode="numeric"
                        autoComplete="cc-exp"
                      />
                    </div>
                    <div className="w-24">
                      <Field
                        label={t("checkout.form.card.ccv")}
                        value={cardCcv}
                        onChange={(v) => setCardCcv(v.replace(/\D/g, "").slice(0, 4))}
                        error={errors.cardCcv}
                        inputMode="numeric"
                        autoComplete="cc-csc"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Field
                        label={t("checkout.form.card.postal_code")}
                        value={formatCep(postalCode)}
                        onChange={(v) => setPostalCode(v.replace(/\D/g, "").slice(0, 8))}
                        error={errors.postalCode}
                        inputMode="numeric"
                        autoComplete="postal-code"
                      />
                    </div>
                    <div className="w-28">
                      <Field
                        label={t("checkout.form.card.address_number")}
                        value={addressNumber}
                        onChange={(v) => setAddressNumber(v.slice(0, 10))}
                        error={errors.addressNumber}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                  <p
                    className="text-[10px] font-body leading-snug"
                    style={{ color: "rgba(255,255,255,0.35)" }}
                  >
                    {t("checkout.form.card.security")}
                  </p>
                </>
              )}

              <button
                type="submit"
                className="mt-2 w-full py-3 rounded-xl font-display text-[14px] font-bold tracking-wide transition-all active:scale-[0.97]"
                style={{
                  background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(187 72% 55%))",
                  color: "#fff",
                  boxShadow: "0 0 24px hsl(187 72% 50% / 0.3)",
                }}
              >
                {submitLabel} · {formatBRL(planValue)}
              </button>
            </motion.form>
          )}

          {(stage === "loading" || stage === "awaiting") && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-16 gap-4"
            >
              <Loader2 size={36} className="animate-spin" style={{ color: "hsl(187 72% 55%)" }} />
              <p className="text-[13px] font-body" style={{ color: "hsl(210 20% 70%)" }}>
                {stage === "awaiting" ? t("checkout.pix.waiting") : method === "card" ? t("checkout.loading.card") : t("checkout.loading")}
              </p>
            </motion.div>
          )}

          {stage === "pix" && pix && (
            <motion.div
              key="pix"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="flex flex-col items-center gap-5"
            >
              <h2 className="font-display text-[18px] font-bold text-white">{t("checkout.pix.title")}</h2>
              <p className="text-[12px] font-body text-center" style={{ color: "hsl(210 20% 65%)" }}>
                {t("checkout.pix.subtitle")}
              </p>

              <div
                className="rounded-2xl p-3"
                style={{
                  background: "#fff",
                  boxShadow: "0 0 32px hsl(187 72% 50% / 0.18)",
                  border: "1.5px solid hsl(187 72% 50% / 0.4)",
                }}
              >
                <img
                  src={"data:image/png;base64," + pix.encodedImage}
                  alt="PIX QR"
                  width={256}
                  height={256}
                  style={{ display: "block" }}
                />
              </div>

              <div className="w-full">
                <div
                  className="rounded-xl p-3 text-[11px] font-mono break-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "hsl(210 20% 75%)",
                    maxHeight: 96,
                    overflow: "auto",
                  }}
                >
                  {pix.payload}
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="mt-2 w-full py-2.5 rounded-xl font-display text-[12px] font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.97]"
                  style={{
                    background: copied ? "hsl(187 72% 50% / 0.18)" : "rgba(255,255,255,0.05)",
                    border: "1px solid hsl(187 72% 50% / 0.3)",
                    color: copied ? "hsl(187 72% 70%)" : "rgba(255,255,255,0.85)",
                  }}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? t("checkout.pix.copied") : t("checkout.pix.copy")}
                </button>
              </div>

              <div className="flex flex-col items-center gap-1.5">
                <div className="flex items-center gap-2">
                  <span
                    className="block w-2 h-2 rounded-full animate-pulse"
                    style={{ background: "hsl(187 72% 55%)", boxShadow: "0 0 12px hsl(187 72% 55% / 0.6)" }}
                  />
                  <span className="text-[12px] font-body" style={{ color: "hsl(210 20% 75%)" }}>
                    {t("checkout.pix.waiting")}
                  </span>
                </div>
                {expiresLabel && (
                  <span className="text-[11px] font-body" style={{ color: "rgba(255,255,255,0.4)" }}>
                    {tf("checkout.pix.expires", { time: expiresLabel })}
                  </span>
                )}
              </div>
            </motion.div>
          )}

          {stage === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-12 gap-4 text-center"
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(187 72% 55%))",
                  boxShadow: "0 0 36px hsl(187 72% 50% / 0.5)",
                }}
              >
                <Check size={32} className="text-white" strokeWidth={2.5} />
              </div>
              <h2 className="font-display text-[22px] font-bold text-white">
                {t("checkout.success.title")}
              </h2>
              <p className="text-[13px] font-body" style={{ color: "hsl(210 20% 70%)" }}>
                {t("checkout.success.subtitle")}
              </p>
            </motion.div>
          )}

          {stage === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-12 gap-4 text-center"
            >
              <p className="text-[14px] font-body text-white/85">
                {errorMessage || t("checkout.error.generic")}
              </p>
              <button
                onClick={handleRetry}
                className="px-5 py-2.5 rounded-xl font-display text-[13px] font-bold transition-all active:scale-[0.97]"
                style={{
                  background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(187 72% 55%))",
                  color: "#fff",
                  boxShadow: "0 0 24px hsl(187 72% 50% / 0.3)",
                }}
              >
                {errorIsAlreadyPremium ? t("checkout.success.title") : t("checkout.error.retry")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ── Method toggle ──────────────────────────────────────────────────────
const MethodToggle: React.FC<{
  method: PaymentMethod;
  setMethod: (m: PaymentMethod) => void;
  t: (k: string) => string;
}> = ({ method, setMethod, t }) => {
  return (
    <div className="flex flex-col gap-1.5">
      <span
        className="text-[10px] font-display tracking-[0.18em] uppercase"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        {t("checkout.method.toggle")}
      </span>
      <div
        className="grid grid-cols-2 gap-2 p-1 rounded-xl"
        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        <MethodOption
          active={method === "card"}
          onClick={() => setMethod("card")}
          icon={<CreditCard size={14} />}
          label={t("checkout.method.card")}
        />
        <MethodOption
          active={method === "pix"}
          onClick={() => setMethod("pix")}
          icon={<QrCode size={14} />}
          label={t("checkout.method.pix")}
        />
      </div>
    </div>
  );
};

const MethodOption: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-display text-[12px] font-semibold transition-all active:scale-[0.98]"
      style={{
        background: active ? "linear-gradient(135deg, hsl(187 72% 45% / 0.25), hsl(187 72% 55% / 0.15))" : "transparent",
        border: active ? "1px solid hsl(187 72% 55% / 0.5)" : "1px solid transparent",
        color: active ? "hsl(187 72% 75%)" : "rgba(255,255,255,0.65)",
      }}
    >
      {icon}
      {label}
    </button>
  );
};

// ── Card formatting helpers ────────────────────────────────────────────
function formatCardNumber(raw: string): string {
  const d = raw.replace(/\D/g, "");
  return d.match(/.{1,4}/g)?.join(" ") || "";
}
function formatExp(raw: string): string {
  return raw;
}
function formatExpEdit(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 4);
  if (d.length <= 2) return d;
  return d.slice(0, 2) + "/" + d.slice(2);
}
function formatCep(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length <= 5) return d;
  return d.slice(0, 5) + "-" + d.slice(5, 8);
}

// ── Generic field ──────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  inputMode?: "text" | "numeric" | "tel" | "email";
  autoComplete?: string;
  readOnly?: boolean;
  disabled?: boolean;
  optional?: boolean;
}

const Field: React.FC<FieldProps> = ({
  label, value, onChange, error, inputMode, autoComplete, readOnly, disabled, optional,
}) => {
  return (
    <label className="flex flex-col gap-1.5">
      <span
        className="text-[10px] font-display tracking-[0.18em] uppercase"
        style={{ color: optional ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.55)" }}
      >
        {label}
      </span>
      <input
        type="text"
        inputMode={inputMode}
        autoComplete={autoComplete}
        readOnly={readOnly}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg font-body text-[14px] outline-none transition-colors"
        style={{
          background: disabled ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)",
          border: error ? "1px solid hsl(0 70% 55% / 0.6)" : "1px solid rgba(255,255,255,0.08)",
          color: disabled ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.92)",
        }}
      />
      {error && (
        <span className="text-[11px] font-body" style={{ color: "hsl(0 70% 65%)" }}>
          {error}
        </span>
      )}
    </label>
  );
};

export default CheckoutPremium;
